import {
	createContext,
	forwardRef,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { TableVirtuoso } from "react-virtuoso";
import { Button, Label, UncontrolledTooltip } from "reactstrap";
import moment from "moment";
import jsPDF from "jspdf";

import "../../styles/StackPlots.css";
import { default as utilIcons } from "../../resources/utilIcons";
import { colors } from "../../styles/colors";

import StackPlot from "./StackPlot";
import { data as dummyData, config as dummyConfig } from "./StackPlotDummy";
import { RestContext } from "../../App";
import { DATA_URL, STACKPLOT_URL } from "../../serverConfig";
import { checkMobile, isNumeric } from "../../util/globals";
import SearchableSelect from "../../components/SearchableSelect";

const INTERVALS = {
	// Number of seconds between data ticks
	hourly: 60 * 60, // 1 hour
	daily: 60 * 60, // 1 hour
	cumulative: 60 * 60, // 1 hour
	"24-hour run": 60 * 60, // 1 hour
	"1-min": 60, // 1 minute
	"5-min": 5 * 60, // 5 minutes
	"15-min": 15 * 60, // 15 minutes
};

function StackPlots({ selectedSite }) {
	const [columns, setColumns] = useState([]);
	const [data, setData] = useState([]);
	const [config, setConfig] = useState();
	const [stackplotData, setStackplotData] = useState({});
	const [configList, setConfigList] = useState([]);
	const [selectedConfig, setSelectedConfig] = useState();
	const [selectedOptions, setSelectedOptions] = useState({
		selectedSite: selectedSite,
		configId: null,
		precision: "hourly",
		datesToLoad: { minTimestamp: null, maxTimestamp: null },
		sameConfig: false,
	});
	const [loading, setLoading] = useState(false);
	const [loadingProgress, setLoadingProgress] = useState(1);
	const [dateRange, setDateRange] = useState({});
	const [printMode, setPrintMode] = useState(false);
	const [chartRefs, setChartRefs] = useState();
	const [callbacks, setCallbacks] = useState({});
	const [popoutWindow, setPopoutWindow] = useState(null);
	const [zoomLevel, setZoomLevel] = useState(100);
	const [popoutZoomLevel, setPopoutZoomLevel] = useState(100);

	const { sendGetRequest, sendPostRequest } = useContext(RestContext);

	const loadingOptions = useRef(loading);
	const stackPlotRef = useRef(null);

	const togglePrintMode = () => setPrintMode(!printMode);

	useEffect(() => {
		if (config) {
			const today = moment().hour(0).minute(0).second(0).millisecond(0);
			setSelectedOptions((values) => ({
				...values,
				precision: config.precision || "hourly",
				configId: config.stackPlotId,
				datesToLoad: {
					minTimestamp: moment(today)
						.subtract(config.defaultSpan || 7, "day")
						.unix(),
					maxTimestamp: moment().unix(),
				},
				sameConfig: false,
			}));
		}
	}, [config]);

	useEffect(() => {
		if (config && selectedOptions) {
			if (selectedSite === "all") {
				const responseData = dummyData;
				setColumns(responseData.columns);
				setData(responseData.data);
			} else {
				beginLoad();
			}
		}
	}, [selectedOptions]);

	useEffect(() => {
		if (selectedSite !== "all") {
			setSelectedOptions((values) => ({
				...values,
				selectedSite: selectedSite,
			}));
			sendGetRequest(
				STACKPLOT_URL + "/list/" + selectedSite,
				(response) => {
					const responseData = response.data;
					responseData.sort((a, b) =>
						a.plotName > b.plotName ? 1 : -1,
					);
					setConfigList(responseData);
					if (responseData && responseData.length) {
						setSelectedConfig(responseData[0].stackPlotId);
					} else {
						setSelectedConfig();
					}
				},
			);
		} else {
			setConfigList([]);
			setConfig(dummyConfig);
		}
	}, [selectedSite]);

	useEffect(() => {
		if (selectedConfig) {
			sendGetRequest(
				STACKPLOT_URL + "/config/" + selectedConfig,
				(response) => {
					setConfig(response.data);
				},
			);
		}
	}, [selectedConfig]);

	useEffect(() => {
		if (loading) {
			loadInfo();
		} else {
			setLoadingProgress(0);
		}
	}, [loading]);

	useEffect(() => {
		setStackplotData(explodeData(data));
	}, [data, selectedOptions]);

	useEffect(() => {
		setDateRange({ min: getMinDate(), max: getMaxDate() });
	}, [stackplotData]);

	useEffect(() => {
		return () => {
			const childWindow = popoutWindow?.ownerDocument?.defaultView;

			if (childWindow && !childWindow.closed) {
				childWindow.close();
			}
		};
	}, [popoutWindow]);

	useEffect(() => {
		if (!popoutWindow || !config?.charts?.length) {
			return;
		}

		const popupDocument = popoutWindow.ownerDocument;
		const popupWindow = popupDocument.defaultView;

		if (!popupWindow) {
			return;
		}

		const updatePopoutZoom = () => {
			const controls = popupDocument.getElementById(
				"stackplot-popout-controls",
			);

			const help = popupDocument.getElementById("stackplot-popout-help");

			const controlsHeight =
				(controls?.getBoundingClientRect().height || 0) +
				(help?.getBoundingClientRect().height || 0);

			const calculatedZoom = calculatePopoutZoom(
				popupWindow,
				config,
				controlsHeight,
			);

			setPopoutZoomLevel(calculatedZoom);
		};

		const frameId = popupWindow.requestAnimationFrame(updatePopoutZoom);

		return () => {
			popupWindow.cancelAnimationFrame(frameId);
		};
	}, [popoutWindow, config]);

	const beginLoad = (dataOptions) => {
		if (!selectedOptions || !config || !config.parCodes) return;
		if (!dataOptions) {
			dataOptions = {
				selectedSite: selectedOptions.selectedSite,
				precision: selectedOptions.precision,
				sameConfig: selectedOptions.sameConfig,
				loadedMin: selectedOptions.sameConfig ? getLoadedMin() : null,
				loadedMax: selectedOptions.sameConfig ? getLoadedMax() : null,
				minTimestamp: selectedOptions.datesToLoad.minTimestamp,
				maxTimestamp: selectedOptions.datesToLoad.maxTimestamp,
				dataType: config.dataType,
				parCodes: config.parCodes,
				calPars: config.calPars,
			};
		}
		if (!loading) {
			setLoading(dataOptions);
		}
	};

	const reloadData = () => {
		if (!selectedOptions || !config || !config.parCodes || !config.calPars)
			return;
		const dataOptions = {
			selectedSite: selectedOptions.selectedSite,
			precision: selectedOptions.precision,
			sameConfig: selectedOptions.sameConfig,
			loadedMin: null,
			loadedMax: null,
			minTimestamp: selectedOptions.datesToLoad.minTimestamp,
			maxTimestamp: selectedOptions.datesToLoad.maxTimestamp,
			dataType: config.dataType,
			parCodes: config.parCodes,
			calPars: config.calPars,
		};
		beginLoad(dataOptions);
	};

	const loadInfo = () => {
		if (!selectedOptions) return;
		loadingOptions.current = loading;
		if (loading) {
			if (
				loading.loadedMin &&
				loading.loadedMin < selectedOptions.datesToLoad.minTimestamp
			) {
				setLoading(null);
				return;
			}
			if (config?.dataType?.match(/.*CAL/)) {
				const payload = {
					precision: loading.precision,
					minTimestamp: loading.minTimestamp,
					maxTimestamp: loading.maxTimestamp,
					loadedMin: loading.loadedMin,
					loadedMax: loading.loadedMax,
					dataType: loading.dataType,
					calPars: loading.calPars,
				};
				sendPostRequest(
					`${DATA_URL}/stackplot/info/${loading.selectedSite}`,
					payload,
					(response) => {
						const numPages = response.data.numPages;
						const pageSize = response.data.pageSize;
						setLoadingProgress(0);
						if (numPages > 0) {
							setColumns(response.data.columns);
							if (loading.sameConfig) {
								loadPage(1, numPages, pageSize, loading, data);
							} else {
								loadPage(1, numPages, pageSize, loading);
							}
						} else {
							if (!loading.sameConfig) {
								setColumns([]);
								setData({});
							}
							setLoading(null);
						}
					},
					(error) => {
						setLoading(null);
					},
				);
			} else {
				const payload = {
					precision: loading.precision,
					minTimestamp: loading.minTimestamp,
					maxTimestamp: loading.maxTimestamp,
					loadedMin: loading.loadedMin,
					loadedMax: loading.loadedMax,
					dataType: loading.dataType,
					parCodes: loading.parCodes,
				};
				sendPostRequest(
					`${DATA_URL}/stackplot/info/${loading.selectedSite}`,
					payload,
					(response) => {
						const numPages = response.data.numPages;
						const pageSize = response.data.pageSize;
						setLoadingProgress(0);
						if (numPages > 0) {
							setColumns(response.data.columns);
							if (loading.sameConfig) {
								loadPage(1, numPages, pageSize, loading, data);
							} else {
								loadPage(1, numPages, pageSize, loading);
							}
						} else {
							if (!loading.sameConfig) {
								setColumns([]);
								setData({});
							}
							setLoading(null);
						}
					},
					(error) => {
						setLoading(null);
					},
				);
			}
		}
	};

	const loadPage = (
		pageNumber,
		numPages,
		pageSize,
		dataOptions,
		tempData = {},
	) => {
		if (
			JSON.stringify(loadingOptions.current) ===
			JSON.stringify(dataOptions)
		) {
			if (config?.dataType?.match(/.*CAL/)) {
				const payload = {
					stackplotId: selectedConfig,
					precision: dataOptions.precision,
					page: pageNumber,
					pageSize: pageSize,
					numPages: numPages,
					loadedMin: dataOptions.loadedMin,
					loadedMax: dataOptions.loadedMax,
					minTimestamp: dataOptions.minTimestamp,
					maxTimestamp: dataOptions.maxTimestamp,
					dataType: dataOptions.dataType,
					calPars: dataOptions.calPars,
				};
				sendPostRequest(
					`${DATA_URL}/stackplot/cal/${selectedSite}`,
					payload,
					(response) => {
						if (
							JSON.stringify(loadingOptions.current) ===
							JSON.stringify(dataOptions)
						) {
							const responseData = response.data;

							tempData = { ...tempData, ...responseData.data };
							setData(tempData);
							setLoadingProgress(pageNumber / numPages);
							if (pageNumber < numPages) {
								loadPage(
									pageNumber + 1,
									numPages,
									pageSize,
									dataOptions,
									tempData,
								);
							} else {
								setLoading(null);
								if (!selectedOptions.sameConfig) {
									setSelectedOptions((values) => ({
										...values,
										sameConfig: true,
									}));
								}
							}
						}
					},
					(error) => {
						setLoading(null);
					},
				);
			} else {
				const payload = {
					stackplotId: selectedConfig,
					precision: dataOptions.precision,
					page: pageNumber,
					pageSize: pageSize,
					numPages: numPages,
					loadedMin: dataOptions.loadedMin,
					loadedMax: dataOptions.loadedMax,
					minTimestamp: dataOptions.minTimestamp,
					maxTimestamp: dataOptions.maxTimestamp,
					dataType: dataOptions.dataType,
					parCodes: dataOptions.parCodes,
				};
				sendPostRequest(
					`${DATA_URL}/stackplot/${selectedSite}`,
					payload,
					(response) => {
						if (
							JSON.stringify(loadingOptions.current) ===
							JSON.stringify(dataOptions)
						) {
							const responseData = response.data;

							tempData = { ...tempData, ...responseData.data };
							setData(tempData);
							setLoadingProgress(pageNumber / numPages);
							if (pageNumber < numPages) {
								loadPage(
									pageNumber + 1,
									numPages,
									pageSize,
									dataOptions,
									tempData,
								);
							} else {
								setLoading(null);
								if (!selectedOptions.sameConfig) {
									setSelectedOptions((values) => ({
										...values,
										sameConfig: true,
									}));
								}
							}
						}
					},
					(error) => {
						setLoading(null);
					},
				);
			}
		}
	};

	const getLoadedMin = () => {
		if (!data) return null;
		const timestamps = Object.keys(data);
		if (!timestamps.length) return null;
		return getMin(timestamps);
	};

	const getLoadedMax = () => {
		if (!data) return null;
		const timestamps = Object.keys(data);
		if (!timestamps.length) return null;
		return getMax(timestamps);
	};

	const getMinDate = () => {
		if (!stackplotData || !stackplotData._timestamps) return 0;
		return moment
			.unix(stackplotData._timestamps[0])
			.format("YYYY-MM-DDTHH:mm");
	};

	const getMaxDate = () => {
		if (!stackplotData || !stackplotData._timestamps) return 0;
		return moment
			.unix(
				stackplotData._timestamps[stackplotData._timestamps.length - 1],
			)
			.format("YYYY-MM-DDTHH:mm");
	};

	const getMin = (data) => {
		if (data.length === 0) return -Infinity;
		else if (data.length === 1) return data[0];
		else {
			let min = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] < min) min = data[i];
			}
			return min;
		}
	};

	const getMax = (data) => {
		if (data.length === 0) return Infinity;
		else if (data.length === 1) return data[0];
		else {
			let max = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] > max) max = data[i];
			}
			return max;
		}
	};

	const explodeData = (rawData) => {
		if (!config?.dataType || !rawData || !columns) return null;
		const explodedData = {};
		if (config?.dataType?.match(/.*CAL/)) {
			for (let i = 0; i < columns.length; i++) {
				if (!Array.isArray(columns[i])) return null;
				if (!columns[i][0].match(/_(FLAG|CODE)$/)) {
					explodedData[columns[i]] = {
						columnIndex: i,
					};
				}
			}
		} else {
			for (let i = 0; i < columns.length; i++) {
				if (Array.isArray(columns[i])) return null;
				if (!columns[i].match(/_(FLAG|CODE)$/)) {
					explodedData[columns[i]] = {
						columnIndex: i,
					};
				}
			}
		}

		let minTimestamp = selectedOptions.datesToLoad.minTimestamp;
		let maxTimestamp = selectedOptions.datesToLoad.maxTimestamp;
		let timestamps = [];
		let interval = INTERVALS[config?.precision || "hourly"];
		for (let i = 0; i <= (maxTimestamp - minTimestamp) / interval; i++) {
			timestamps.push(minTimestamp + i * interval);
		}

		for (let timestamp of timestamps) {
			let row = rawData[timestamp];
			if (row) {
				for (let parCode in explodedData) {
					explodedData[parCode][timestamp] = [
						row[explodedData[parCode].columnIndex],
						row[explodedData[parCode].columnIndex + 1],
					];
				}
			}
		}
		explodedData._timestamps = timestamps;
		return explodedData;
	};

	const doXScroll = (scrollOptions) => {
		if (
			!dateRange ||
			dateRange.min === undefined ||
			dateRange.max === undefined ||
			!stackplotData ||
			!stackplotData._timestamps ||
			stackplotData._timestamps.length < 2
		)
			return;
		const { direction, quantity, unit } = scrollOptions;
		let offset = 0;
		switch (direction) {
			case "left":
				let dataMin = moment.unix(stackplotData._timestamps[0]);
				let currentMin = moment(dateRange.min, "YYYY-MM-DDTHH:mm");
				let targetMin = moment(currentMin).subtract(quantity, unit);
				if (targetMin.unix() < dataMin.unix()) {
					offset = currentMin.unix() - dataMin.unix();
				} else {
					offset = currentMin.unix() - targetMin.unix();
				}
				setDateRange({
					min: currentMin
						.subtract(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
					max: moment(dateRange.max, "YYYY-MM-DDTHH:mm")
						.subtract(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
				});
				return;
			case "right":
				let dataMax = moment.unix(
					stackplotData._timestamps[
						stackplotData._timestamps.length - 1
					],
				);
				let currentMax = moment(dateRange.max, "YYYY-MM-DDTHH:mm");
				let targetMax = moment(currentMax).add(quantity, unit);
				if (targetMax.unix() > dataMax.unix()) {
					offset = dataMax.unix() - currentMax.unix();
				} else {
					offset = targetMax.unix() - currentMax.unix();
				}
				setDateRange({
					min: moment(dateRange.min, "YYYY-MM-DDTHH:mm")
						.add(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
					max: currentMax
						.add(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
				});
				return;
			default:
				return;
		}
	};

	const updateTimeframe = (minTimestamp, maxTimestamp) => {
		setSelectedOptions((values) => ({
			...values,
			datesToLoad: {
				minTimestamp: minTimestamp,
				maxTimestamp: maxTimestamp,
			},
		}));
	};

	const handleConfigSelect = (value) => {
		const plot = configList.find((p) => p.stackPlotId === value);
		setSelectedConfig(value);
	};

	const handleMinDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, min: value }));
	};

	const handleMaxDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, max: value }));
	};

	const clearCursors = () => {
		for (let pair of chartRefs) {
			const chart = pair[1].current;
			if (chart.options.plugins.annotation) {
				chart.options.plugins.annotation.annotations = [];
				chart.update();
			}
		}
	};

	const setChartDPI = (scale) => {
		if (chartRefs) {
			for (let index of chartRefs.keys()) {
				const chart = chartRefs.get(index)?.current || null;
				if (chart) {
					chart.options.devicePixelRatio = scale;
				} else {
					chartRefs.delete(index);
				}
			}
		}
	};

	const generatePdf = async () => {
		const foundStackPlot = stackPlotRef?.current;
		if (!foundStackPlot) return;
		const pages = foundStackPlot.querySelector(
			".stackplot-pages-container",
		);
		const doc = new jsPDF({
			orientation: "portrait",
			format: [850, 1100], // 8.5 x 11 at 300 DPI
			unit: "px",
		});

		setChartDPI(3);

		await doc.html(pages, {
			x: 0,
			y: 0,
			autoPaging: "text",
		});

		setChartDPI(2);
		doc.deletePage(pages.children.length + 1); // Cleanup extra page created during loading
		return doc;
	};

	const downloadPdf = async () => {
		clearCursors();
		const doc = await generatePdf();
		doc.save(`${config?.fullTitle || ""}_${moment().format("MM-DD-YYYY")}`);
	};

	const printPdf = async () => {
		clearCursors();
		const doc = await generatePdf();
		doc.autoPrint({
			variant: navigator.userAgent.includes("Chrome")
				? "non-conform"
				: "javascript",
		});
		window.open(
			doc.output(`bloburl`, {
				filename: `${config?.fullTitle || ""}_${moment().format(
					"MM-DD-YYYY",
				)}`,
			}),
		);
	};

	const registerCallback = (callbackName, callbackFn) => {
		setCallbacks((values) => ({ ...values, [callbackName]: callbackFn }));
	};

	const getStackPlotNaturalHeight = (plotConfig) => {
		const charts = plotConfig?.charts || [];

		if (!charts.length) {
			return 0;
		}

		const wrapperBorderWidth = 1;
		const labelHeight = 15;
		const labelBottomMargin = -3;
		const labelEffectiveHeight = labelHeight + labelBottomMargin;

		const configuredSpacing = Number(plotConfig.chartSpacing) || 20;

		/*
		 * StackPlot chart spacing includes space occupied by chart borders
		 * and parameter labels. Remove that fixed content so that the
		 * calculated height matches the actual rendered plot more closely.
		 */
		const effectiveSpacing = Math.max(
			0,
			configuredSpacing - 2 * wrapperBorderWidth - labelEffectiveHeight,
		);

		const chartsHeight = charts.reduce(
			(total, chart) => total + (Number(chart.chartHeight) || 200),
			0,
		);

		/*
		 * Spacing only exists between charts.
		 */
		const spacingHeight = Math.max(0, charts.length - 1) * effectiveSpacing;

		const xAxisHeight = 40;
		const titleHeight = plotConfig.fullTitle ? 25 : 0;
		const footnoteHeight = plotConfig.footnote ? 25 : 0;

		return (
			chartsHeight +
			spacingHeight +
			xAxisHeight +
			titleHeight +
			footnoteHeight
		);
	};

	const calculatePopoutZoom = (
		popupWindow,
		plotConfig,
		controlsHeight = 45,
	) => {
		const availableHeight = Math.max(
			1,
			popupWindow.innerHeight - controlsHeight,
		);

		const naturalHeight = getStackPlotNaturalHeight(plotConfig);

		if (naturalHeight <= 0 || naturalHeight <= availableHeight) {
			return 100;
		}

		const calculatedZoom = (availableHeight / naturalHeight) * 100;

		/*
		 * Keep the automatic zoom at a multiple of 10%.
		 */
		const truncatedZoom = Math.floor(calculatedZoom / 10) * 10;

		return Math.max(10, Math.min(100, truncatedZoom));
	};

	const handlePopout = () => {
		if (popoutWindow) {
			const existingWindow = popoutWindow.ownerDocument?.defaultView;

			if (existingWindow && !existingWindow.closed) {
				existingWindow.focus();
				return;
			}

			setPopoutWindow(null);
		}

		const width = window.screen.availWidth - 100;
		const height = window.screen.availHeight - 100;

		const newWindow = window.open(
			"",
			"stackplot-popout",
			[
				`width=${Math.max(width, 800)}`,
				`height=${Math.max(height, 600)}`,
				"resizable=yes",
				"scrollbars=no",
			].join(","),
		);

		if (!newWindow) {
			alert("Popup blocked! Please allow popups for this site.");
			return;
		}

		newWindow.document.title =
			config?.plotName || config?.fullTitle || "Stack Plot";

		document
			.querySelectorAll('style, link[rel="stylesheet"]')
			.forEach((node) => {
				newWindow.document.head.appendChild(node.cloneNode(true));
			});

		newWindow.document.body.style.margin = "0";
		newWindow.document.body.style.width = "100%";
		newWindow.document.body.style.height = "100vh";
		newWindow.document.body.style.overflow = "hidden";

		const contentDiv = newWindow.document.createElement("div");

		contentDiv.id = "stackplot-popout-root";
		contentDiv.style.width = "100%";
		contentDiv.style.height = "100vh";

		newWindow.document.body.appendChild(contentDiv);

		newWindow.requestAnimationFrame(() => {
			const initialZoom = calculatePopoutZoom(newWindow, config);

			setPopoutZoomLevel(initialZoom);
			setPopoutWindow(contentDiv);

			if (callbacks.setTableSize) {
				callbacks.setTableSize("full");
			}
		});

		newWindow.addEventListener("beforeunload", () => {
			setPopoutWindow(null);
			setPopoutZoomLevel(100);

			if (callbacks.setTableSize) {
				callbacks.setTableSize("half");
			}
		});
	};

	return (
		<div id="stackplots">
			<div id="stackplots-controls">
				<div className="stackplot-control-wrapper">
					<Label>Select Configuration:</Label>

					<SearchableSelect
						className={"stackplot-config-select"}
						data={configList}
						selected={selectedConfig}
						setSelected={handleConfigSelect}
						valField={"stackPlotId"}
						textField={"plotName"}
						groupField={"dataType"}
					/>
				</div>

				<div className="stackplot-control-wrapper">
					<Label>Timeframe:</Label>
					<StackPlotTimeFrameSelector
						config={config}
						updateTimeframe={updateTimeframe}
					/>
				</div>
				<div id="x-zoom-controls" className="stackplot-control-wrapper">
					<Label>View:</Label>
					<input
						type="datetime-local"
						onChange={handleMinDate}
						min={getMinDate()}
						max={dateRange.max || getMaxDate()}
						step={
							selectedOptions
								? selectedOptions.precision.match(/\d+-min/)
									? 60
									: 3600
								: 1
						}
						value={dateRange.min || ""}
					/>
					<span>-</span>
					<input
						type="datetime-local"
						onChange={handleMaxDate}
						min={dateRange.min || getMinDate()}
						max={getMaxDate()}
						step={
							selectedOptions
								? selectedOptions.precision.match(/\d+-min/)
									? 60
									: 3600
								: 1
						}
						value={dateRange.max || ""}
					/>
					<DateScroller doXScroll={doXScroll} />
				</div>
				<Button
					id="stackplot-popout-button"
					onClick={handlePopout}
					disabled={!config || !stackplotData}
				>
					Popout
				</Button>
				<Button
					id="stackplots-print-mode-toggle"
					onClick={togglePrintMode}
				>
					{printMode ? "Expanded View" : "Print View"}
				</Button>
				<Button
					id="stackplots-refresh-button"
					onClick={reloadData}
					disabled={loading}
				>
					{utilIcons.refresh(colors["ars-neutral-900"])}
				</Button>
			</div>
			<div id="stackplot-controls-help">
				<div className="stackplot-controls-help-blurb">
					<b>Drag:</b> Pan X Axis (all charts)
				</div>
				<div className="stackplot-controls-help-blurb">
					<b>Ctrl+Scroll:</b> Zoom X Axis (all charts)
				</div>
				<div className="stackplot-controls-help-blurb">
					<b>Shift+Drag</b> Pan Y Axis (single chart)
				</div>
				<div className="stackplot-controls-help-blurb">
					<b>Shift+Scroll:</b> Zoom Y Axis (single chart)
				</div>
				<div className="stackplot-controls-help-blurb">
					<b>Shift+Dbl Click:</b> Reset Y Axis (single chart)
				</div>
			</div>
			{popoutWindow ? (
				createPortal(
					<div id="stackplots" className="stackplot-popout">
						<div id="stackplot-popout-controls">
							<PlotZoomControls
								zoomLevel={popoutZoomLevel}
								setZoomLevel={setPopoutZoomLevel}
							/>
						</div>

						<div
							id="stackplot-popout-help"
							className="stackplot-popout-help"
						>
							<div id="stackplot-controls-help">
								<div className="stackplot-controls-help-blurb">
									<b>Drag:</b> Pan X Axis (all charts)
								</div>

								<div className="stackplot-controls-help-blurb">
									<b>Ctrl+Scroll:</b> Zoom X Axis (all charts)
								</div>

								<div className="stackplot-controls-help-blurb">
									<b>Shift+Drag:</b> Pan Y Axis (single chart)
								</div>

								<div className="stackplot-controls-help-blurb">
									<b>Shift+Scroll:</b> Zoom Y Axis (single
									chart)
								</div>

								<div className="stackplot-controls-help-blurb">
									<b>Shift+Dbl Click:</b> Reset Y Axis (single
									chart)
								</div>
							</div>
						</div>

						<div id="stackplot-outer-wrapper">
							<StackPlot
								data={stackplotData}
								config={config}
								dateRange={dateRange}
								callbacks={callbacks}
								zoomLevel={popoutZoomLevel}
								isPopout={true}
							/>
						</div>

						<LoadingScreen
							hidden={!loading}
							loadingProgress={loadingProgress}
							isMobile={checkMobile()}
						/>
					</div>,
					popoutWindow,
				)
			) : (
				<div id="stackplot-outer-wrapper">
					<div className="stackplot-y-axis-toggle-wrapper">
						<PlotZoomControls
							zoomLevel={zoomLevel}
							setZoomLevel={setZoomLevel}
						/>
					</div>
					<StackPlot
						data={stackplotData}
						config={config}
						dateRange={dateRange}
						stackPlotRef={stackPlotRef}
						printable={printMode}
						bubbleChartRefs={setChartRefs}
						callbacks={callbacks}
						zoomLevel={zoomLevel}
					/>
				</div>
			)}
			<div id="stackplot-print-mode-buttons" hidden={!printMode}>
				<Button
					className="stackplot-download-button"
					onClick={downloadPdf}
				>
					Download
				</Button>
				<Button
					className="stackplot-download-button"
					onClick={printPdf}
				>
					Print
				</Button>
			</div>
			<StackPlotTable
				data={stackplotData}
				config={config}
				registerCallback={registerCallback}
			/>
			<LoadingScreen
				hidden={!loading}
				loadingProgress={loadingProgress}
				isMobile={checkMobile()}
			/>
		</div>
	);
}

export function StackPlotTimeFrameSelector({ config, updateTimeframe }) {
	const [selectedMode, setSelectedMode] = useState("last");
	const [numDays, setNumDays] = useState(7);
	const [dateRange, setDateRange] = useState({ min: null, max: null });

	useEffect(() => {
		init();
	}, [config]);

	useEffect(() => {
		setDateRange({
			min: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.subtract(numDays, "day")
				.format("YYYY-MM-DDTHH:mm"),
			max: moment().format("YYYY-MM-DDTHH:mm"),
		});
	}, [numDays]);

	const init = () => {
		setSelectedMode("last");

		setNumDays(config?.defaultSpan || 7);

		setDateRange({
			min: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.add(-(config?.defaultSpan || 7), "day")
				.format("YYYY-MM-DDTHH:mm"),
			max: moment().format("YYYY-MM-DDTHH:mm"),
		});
	};

	const fireUpdate = (event) => {
		const allowedIds = [
			"stackplot-timeframe-numdays-input",
			"stackplot-timeframe-mindate-input",
			"stackplot-timeframe-maxdate-input",
			"stackplot-timeframe-mode-select",
		];
		if (
			event.relatedTarget?.id &&
			allowedIds.includes(event.relatedTarget.id)
		)
			return;
		if (dateRange.min && dateRange.max) {
			updateTimeframe(
				moment(dateRange.min, "YYYY-MM-DDTHH:mm").unix(),
				moment(dateRange.max, "YYYY-MM-DDTHH:mm").unix(),
			);
		}
	};

	const getControlsForMode = () => {
		switch (selectedMode) {
			case "last":
				return (
					<>
						<input
							id="stackplot-timeframe-numdays-input"
							type="number"
							value={numDays}
							min={1}
							step={1}
							onChange={handleNumDays}
							onBlur={fireUpdate}
						/>
						<span>Days</span>
					</>
				);
			case "from":
				return (
					<>
						<input
							id="stackplot-timeframe-mindate-input"
							type="datetime-local"
							value={dateRange.min}
							onChange={handleMinDate}
							step={3600}
							onBlur={fireUpdate}
						/>
						<span>to</span>
						<input
							id="stackplot-timeframe-maxdate-input"
							type="datetime-local"
							value={dateRange.max}
							onChange={handleMaxDate}
							onBlur={fireUpdate}
						/>
					</>
				);
			default:
				return null;
		}
	};

	const handleChangeMode = (event) => {
		const value = event.target.value;
		setSelectedMode(value);
		if (value === "last") {
			if (dateRange.min && dateRange.max) {
				const diff = Math.ceil(
					moment(dateRange.max, "YYYY-MM-DDT-HH:mm").diff(
						moment(dateRange.min, "YYYY-MM-DDT-HH:mm"),
						"days",
					),
				);
				setNumDays(diff);
			}
		} else {
			return;
		}
	};

	const handleNumDays = (event) => {
		setNumDays(Number(event.target.value));
	};

	const handleMinDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, min: value }));
	};

	const handleMaxDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, max: value }));
	};

	return (
		<div id="stackplot-timeframe-selector">
			<select
				id="stackplot-timeframe-mode-select"
				value={selectedMode}
				onChange={handleChangeMode}
			>
				<option value="last">Last</option>
				<option value="from">From</option>
			</select>
			{getControlsForMode()}
		</div>
	);
}

function DateScroller({ doXScroll }) {
	const [quantity, setQuantity] = useState(1);
	const [unit, setUnit] = useState("days");

	const handleQuantity = (event) => {
		const value = Number(event.target.value);
		setQuantity(value);
	};

	const handleUnit = (event) => {
		const value = event.target.value;
		setUnit(value);
	};

	const handleMinus = () => {
		doXScroll({
			direction: "left",
			quantity,
			unit,
		});
	};

	const handlePlus = () => {
		doXScroll({
			direction: "right",
			quantity,
			unit,
		});
	};

	return (
		<div className="date-scroller">
			<Button onClick={handleMinus}>-</Button>
			<input
				className="time-quantity-input"
				type="number"
				step={1}
				value={quantity}
				onChange={handleQuantity}
			/>
			<select
				className="time-unit-select"
				value={unit}
				onChange={handleUnit}
			>
				<option value="hours">Hour(s)</option>
				<option value="days">Day(s)</option>
				<option value="weeks">Week(s)</option>
				<option value="months">Month(s)</option>
			</select>
			<Button onClick={handlePlus}>+</Button>
		</div>
	);
}

export function StackPlotTable({ data, config, registerCallback }) {
	const [tableData, setTableData] = useState({});
	const [size, setSize] = useState("minimized");
	const [highlightedData, setHighlightedData] = useState({
		chartIndex: null,
		min: null,
		max: null,
	});

	const virtuosoRef = useRef(null);

	useEffect(() => {
		registerCallback("cursor", setHighlightedData);
		registerCallback("setTableSize", setSize);
	}, []);

	useEffect(() => {
		if (data) {
			setTableData(processData(data));
		}
	}, [data, config]);

	useEffect(() => {
		if (
			highlightedData.min !== null &&
			size !== "minimized" &&
			virtuosoRef.current
		) {
			virtuosoRef.current.scrollToIndex({
				index: highlightedData.min,
				align: "center",
				behavior: "auto",
			});
		}
	}, [size, highlightedData]);

	const isCalPlot = () => {
		return !!config?.dataType?.match(/.*CAL/);
	};

	const processData = (data) => {
		if (!config?.charts || !config?.calPars) return data;

		let newTableData = { ...data };

		if (Object.keys(data).some((key) => key.match(/DIFF/))) {
			newTableData = generateDiffDatasets(data);
		}

		return newTableData;
	};

	const generateDiffDatasets = (data) => {
		const newData = { ...data };

		for (let chart of config.charts) {
			let pDiffPar;
			let diffPar;

			const parameterList = chart.parameters;

			if (
				(pDiffPar = parameterList.find(
					(parameter) => parameter.valType === "PDIFF",
				))
			) {
				const isValidated = pDiffPar.parCode.match(/_VAL$/) !== null;

				const actualPar = parameterList.find((par) =>
					isValidated
						? par.parCode.match(/_VAL$/) && par.valType === "ACTUAL"
						: !par.parCode.match(/_VAL$/) &&
							par.valType === "ACTUAL",
				);

				const indicatedPar = parameterList.find((par) =>
					isValidated
						? par.parCode.match(/_VAL$/) &&
							par.valType === "INDICATED"
						: !par.parCode.match(/_VAL$/) &&
							par.valType === "INDICATED",
				);

				if (actualPar && indicatedPar) {
					const pDiffCalPar = [
						pDiffPar.parCode,
						pDiffPar.calType,
						pDiffPar.valType,
					];

					const actualCalPar = [
						actualPar.parCode,
						actualPar.calType,
						actualPar.valType,
					];

					const indicatedCalPar = [
						indicatedPar.parCode,
						indicatedPar.calType,
						indicatedPar.valType,
					];

					const actualData = data[actualCalPar];
					const indicatedData = data[indicatedCalPar];

					if (actualData && indicatedData) {
						newData[pDiffCalPar] = {};

						Object.keys(actualData)
							.sort()
							.filter((key) => key !== "columnIndex")
							.forEach((timestamp) => {
								newData[pDiffCalPar][timestamp] =
									isNumeric(
										actualData[timestamp]
											? actualData[timestamp][0]
											: null,
									) &&
									isNumeric(
										indicatedData[timestamp]
											? indicatedData[timestamp][0]
											: null,
									)
										? (
												(100 *
													(indicatedData[
														timestamp
													][0] -
														actualData[
															timestamp
														][0])) /
												actualData[timestamp][0]
											).toFixed(2)
										: null;
							});
					}
				}
			} else if (
				(diffPar = parameterList.find(
					(parameter) => parameter.valType === "DIFF",
				))
			) {
				const isValidated = diffPar.parCode.match(/_VAL$/) !== null;

				const actualPar = parameterList.find((par) =>
					isValidated
						? par.parCode.match(/_VAL$/) && par.valType === "ACTUAL"
						: !par.parCode.match(/_VAL$/) &&
							par.valType === "ACTUAL",
				);

				const indicatedPar = parameterList.find((par) =>
					isValidated
						? par.parCode.match(/_VAL$/) &&
							par.valType === "INDICATED"
						: !par.parCode.match(/_VAL$/) &&
							par.valType === "INDICATED",
				);

				if (actualPar && indicatedPar) {
					const diffCalPar = [
						diffPar.parCode,
						diffPar.calType,
						diffPar.valType,
					];

					const actualCalPar = [
						actualPar.parCode,
						actualPar.calType,
						actualPar.valType,
					];

					const indicatedCalPar = [
						indicatedPar.parCode,
						indicatedPar.calType,
						indicatedPar.valType,
					];

					const actualData = data[actualCalPar];
					const indicatedData = data[indicatedCalPar];

					if (actualData && indicatedData) {
						newData[diffCalPar] = {};

						Object.keys(actualData)
							.sort()
							.filter((key) => key !== "columnIndex")
							.forEach((timestamp) => {
								newData[diffCalPar][timestamp] =
									isNumeric(
										actualData[timestamp]
											? actualData[timestamp][0]
											: null,
									) &&
									isNumeric(
										indicatedData[timestamp]
											? indicatedData[timestamp][0]
											: null,
									)
										? (
												indicatedData[timestamp][0] -
												actualData[timestamp][0]
											).toFixed(actualPar.rawPrec)
										: null;
							});
					}
				}
			}
		}

		return newData;
	};

	const getParCodes = () => {
		return Object.keys(tableData).filter(
			(parCode) => parCode !== "_timestamps",
		);
	};

	const getCalPars = () => {
		return Object.keys(tableData)
			.filter((key) => key !== "_timestamps")
			.map((calPar) => calPar.split(","));
	};

	const getTimestamps = () => {
		if (!tableData) return [];

		if (!isCalPlot()) {
			return tableData._timestamps || [];
		}

		const timestamps = new Set();

		for (const calPar of getCalPars()) {
			const dataset = tableData[calPar];

			if (!dataset) continue;

			Object.keys(dataset).forEach((timestamp) => {
				if (timestamp !== "columnIndex") {
					timestamps.add(timestamp);
				}
			});
		}

		return Array.from(timestamps).sort((a, b) => Number(a) - Number(b));
	};

	const getHighlightedPars = () => {
		const highlightedPars = [];

		if (highlightedData.chartIndex === null) {
			return highlightedPars;
		}

		const highlightedChart = config?.charts?.[highlightedData.chartIndex];

		if (!highlightedChart) {
			return highlightedPars;
		}

		for (const parameter of highlightedChart.parameters) {
			if (isCalPlot()) {
				highlightedPars.push([
					parameter.parCode,
					parameter.calType,
					parameter.valType,
				]);
			} else {
				highlightedPars.push(parameter.parCode);

				if (config.dataType === "VAL") {
					highlightedPars.push(parameter.parCode + "_VAL");
				}
			}
		}

		return highlightedPars;
	};

	const getParameterPrecision = () => {
		const precisionMap = {};

		for (const chart of config?.charts || []) {
			for (const parameter of chart.parameters || []) {
				if (isCalPlot()) {
					precisionMap[
						[
							parameter.parCode,
							parameter.calType,
							parameter.valType,
						]
					] = parameter.rawPrec;
				} else {
					precisionMap[parameter.parCode] = parameter.rawPrec;
				}
			}
		}

		return precisionMap;
	};

	const isHighlighted = (rowIndex) => {
		if (highlightedData.min === null || highlightedData.max === null) {
			return false;
		}

		return (
			rowIndex >= highlightedData.min && rowIndex <= highlightedData.max
		);
	};

	const isHighlightedCalPar = (calPar, highlightedPars) => {
		return highlightedPars.some((highlightedPar) =>
			highlightedPar.every((value, index) => value === calPar[index]),
		);
	};

	const flagString = (par, timestamp, dataObj) => {
		if (!dataObj) return "";

		const flag = dataObj.at(1);

		if (!flag) {
			return "";
		}

		if (flag.match(/[A-Z]+\(.*\)/)) {
			const code = flag.substring(0, flag.indexOf("("));
			const adj = flag.substring(flag.indexOf("(") + 1, flag.length - 1);

			const id = `${par.toString()}-${timestamp}-code`;

			return (
				<>
					(
					<span
						id={id}
						style={{
							color: colors["ars-blue-400"],
							textDecoration: "underline",
						}}
					>
						{code}
					</span>
					)
					<UncontrolledTooltip placement="top" target={id}>
						{adj}
					</UncontrolledTooltip>
				</>
			);
		}

		return ` (${flag})`;
	};

	const getHeaders = () => {
		if (isCalPlot()) {
			return getCalHeaders();
		}

		const parCodes = getParCodes();

		return (
			<>
				<tr>
					<th>Timestamp</th>

					{parCodes.map((parCode) => (
						<th key={`stackplot-table-head-${parCode}`}>
							{parCode}
						</th>
					))}
				</tr>

				<tr>
					<th />

					{parCodes.map((parCode) => {
						const parIndex = config?.parCodes?.indexOf(parCode);

						return (
							<th key={`stackplot-table-head-unit-${parCode}`}>
								{parIndex >= 0
									? config?.unitCodes?.[parIndex]
									: ""}
							</th>
						);
					})}
				</tr>
			</>
		);
	};

	const getCalHeaders = () => {
		const calPars = getCalPars();

		return (
			<>
				<tr>
					<th>Par Code</th>

					{calPars.map((calPar) => (
						<th
							key={`stackplot-table-head-par-${calPar.join("-")}`}
						>
							{calPar[0]}
						</th>
					))}
				</tr>

				<tr>
					<th>Cal Type</th>

					{calPars.map((calPar) => (
						<th
							key={`stackplot-table-head-cal-${calPar.join("-")}`}
						>
							{calPar[1]}
						</th>
					))}
				</tr>

				<tr>
					<th>Val Type</th>

					{calPars.map((calPar) => (
						<th
							key={`stackplot-table-head-val-${calPar.join("-")}`}
						>
							{calPar[2]}
						</th>
					))}
				</tr>

				<tr>
					<th>Timestamp</th>

					{calPars.map((calPar) => {
						const configIndex = config?.calPars?.findIndex(
							(configCalPar) =>
								configCalPar.every(
									(value, index) => value === calPar[index],
								),
						);

						return (
							<th
								key={`stackplot-table-head-unit-${calPar.join(
									"-",
								)}`}
							>
								{configIndex >= 0
									? config?.unitCodes?.[configIndex]
									: ""}
							</th>
						);
					})}
				</tr>
			</>
		);
	};

	const convertToCsvString = () => {
		if (isCalPlot()) return convertCalToCsvString();

		if (!tableData || !config?.parCodes || !config?.charts) {
			return null;
		}

		const parCodes = [];
		const unitCodes = [];

		for (let index in config.parCodes) {
			const parCode = config.parCodes[index];

			if (parCode in tableData) {
				parCodes.push(parCode);
				unitCodes.push(config.unitCodes[index]);

				if (config.dataType === "VAL") {
					parCodes.push(parCode + "_VAL");
					unitCodes.push(config.unitCodes[index]);
				}
			}
		}

		let csvString = "Date,Time";

		for (let parCode of parCodes) {
			csvString += `,${parCode},${parCode}_${
				parCode.match(/_VAL/) ? "CODE" : "FLAG"
			}`;
		}

		csvString += "\n,";

		for (let unitCode of unitCodes) {
			csvString += `,${unitCode},`;
		}

		csvString += "\n";

		const parCodePrec = getParameterPrecision();
		const timestamps = tableData._timestamps || [];

		for (let timestamp of timestamps) {
			const cells = [];

			cells.push(moment.unix(timestamp).format("YYYYMMDD,HHmm"));

			for (let parCode of parCodes) {
				cells.push(
					tableData[parCode]?.[timestamp]
						?.at(0)
						?.toFixed(parCodePrec[parCode]),
				);

				cells.push(tableData[parCode]?.[timestamp]?.at(1));
			}

			csvString += cells.join(",") + "\n";
		}

		return csvString;
	};

	const convertCalToCsvString = () => {
		if (!tableData || !config?.calPars || !config?.charts) {
			return null;
		}

		const calPars = getCalPars();

		let csvString = ",Par Code";

		for (let calPar of calPars) {
			csvString += `,${calPar[0]},${calPar[0]}_${
				calPar[0].match(/_VAL/) ? "CODE" : "FLAG"
			}`;
		}

		csvString += "\n,Cal Type";

		for (let calPar of calPars) {
			csvString += `,${calPar[1]},`;
		}

		csvString += "\n,Val Type";

		for (let calPar of calPars) {
			csvString += `,${calPar[2]},`;
		}

		csvString += "\nDate,Time";

		for (let calPar of calPars) {
			const configIndex = config.calPars.findIndex((configCalPar) =>
				configCalPar.every((value, index) => value === calPar[index]),
			);

			csvString += `,${
				configIndex >= 0 ? config.unitCodes[configIndex] : ""
			},`;
		}

		csvString += "\n";

		const calParPrec = getParameterPrecision();

		for (let timestamp of getTimestamps()) {
			const cells = [];

			cells.push(moment.unix(timestamp).format("YYYYMMDD,HHmm"));

			for (let calPar of calPars) {
				const value = tableData[calPar]?.[timestamp];

				if (Array.isArray(value)) {
					cells.push(value.at(0)?.toFixed(calParPrec[calPar]));
					cells.push(value.at(1));
				} else {
					cells.push(value);
					cells.push("");
				}
			}

			csvString += cells.join(",") + "\n";
		}

		return csvString;
	};

	const exportCSV = () => {
		const csvString = convertToCsvString();

		if (csvString === null) return;

		const file = new Blob([csvString], {
			type: "text/csv",
		});

		const link = document.createElement("a");
		const url = URL.createObjectURL(file);
		const dateString = moment().format("YYYYMMDD");
		const nameString =
			config?.plotName?.replace(/[\s\-]+/g, "_") || "PLOT_DATA";

		link.href = url;
		link.download = `${dateString}_${nameString}.csv`;

		document.body.appendChild(link);
		link.click();

		setTimeout(() => {
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		}, 0);
	};

	return (
		<div id="stackplot-table-wrapper" className={size}>
			<div id="stackplot-table-controls">
				<span>Table View</span>

				<Button
					id="stackplot-table-export"
					onClick={exportCSV}
					hidden={size === "minimized"}
				>
					Export
				</Button>

				<Button onClick={() => setSize("minimized")}>
					{utilIcons.minimized(colors["ars-neutral-900"])}
				</Button>

				<Button onClick={() => setSize("half")}>
					{utilIcons.halfScreen(colors["ars-neutral-900"])}
				</Button>

				<Button onClick={() => setSize("full")}>
					{utilIcons.fullScreen(colors["ars-neutral-900"])}
				</Button>
			</div>

			<div id="stackplot-table" style={{ height: "100%" }}>
				<TableVirtuoso
					ref={virtuosoRef}
					style={{ height: "100%", width: "100%" }}
					data={getTimestamps()}
					fixedHeaderContent={getHeaders}
					components={{
						Table: (props) => (
							<table
								{...props}
								style={{
									...props.style,
									tableLayout: "fixed",
								}}
							/>
						),
						TableRow: StackPlotTableRow,
					}}
					context={{
						tableData,
						config,
						isCalPlot: isCalPlot(),
						parCodes: getParCodes(),
						calPars: getCalPars(),
						highlightedPars: getHighlightedPars(),
						precisionMap: getParameterPrecision(),
						isHighlighted,
						isHighlightedCalPar,
						flagString,
					}}
				/>
			</div>
		</div>
	);
}

const StackPlotTableRow = forwardRef(
	({ item: timestamp, context, ...props }, ref) => {
		const rowIndex = props["data-index"];

		const {
			tableData,
			isCalPlot,
			parCodes,
			calPars,
			highlightedPars,
			precisionMap,
			isHighlighted,
			isHighlightedCalPar,
			flagString,
		} = context;

		if (isCalPlot) {
			return (
				<tr
					ref={ref}
					{...props}
					id={`stackplot-table-row-${rowIndex}`}
					className={
						"stackplot-table-row" +
						(isHighlighted(rowIndex) ? " highlighted-row" : "")
					}
				>
					<td>{moment.unix(timestamp).format("MMM D hh:mm a")}</td>

					{calPars.map((calPar) => {
						const value = tableData[calPar]?.[timestamp];

						const isDifference =
							calPar[2]?.match(/P?DIFF/) !== null;

						return (
							<td
								key={`${timestamp}-${calPar.join("-")}`}
								className={
									isHighlightedCalPar(calPar, highlightedPars)
										? "highlighted"
										: ""
								}
							>
								{isDifference
									? value === null || value === undefined
										? "-"
										: value
									: isNumeric(value?.at(0))
										? value
												.at(0)
												.toFixed(
													precisionMap[calPar] ?? 0,
												)
										: "-"}

								{!isDifference &&
									flagString(calPar, timestamp, value)}
							</td>
						);
					})}
				</tr>
			);
		}

		return (
			<tr
				ref={ref}
				{...props}
				id={`stackplot-table-row-${rowIndex}`}
				className={
					"stackplot-table-row" +
					(isHighlighted(rowIndex) ? " highlighted-row" : "")
				}
			>
				<td>{moment.unix(timestamp).format("MMM D hh:mm a")}</td>

				{parCodes.map((parCode) => {
					const value = tableData[parCode]?.[timestamp];

					return (
						<td
							key={`${timestamp}-${parCode}`}
							className={
								highlightedPars.includes(parCode)
									? "highlighted"
									: ""
							}
						>
							{isNumeric(value?.at(0))
								? value
										.at(0)
										.toFixed(precisionMap[parCode] ?? 0)
								: "-"}

							{flagString(parCode, timestamp, value)}
						</td>
					);
				})}
			</tr>
		);
	},
);

export function PlotZoomControls({ zoomLevel, setZoomLevel }) {
	const zoomIn = () => {
		if (zoomLevel >= 200) return;

		setZoomLevel(zoomLevel + 10);
	};

	const zoomOut = () => {
		if (zoomLevel <= 10) return;

		setZoomLevel(zoomLevel - 10);
	};

	return (
		<div id="plot-zoom-controls">
			<span>Zoom:</span>

			<Button
				className="plot-zoom-control-button"
				disabled={zoomLevel <= 10}
				onClick={zoomOut}
			>
				-
			</Button>

			<span
				style={{
					width: "4ch",
					textAlign: "center",
				}}
			>
				{`${zoomLevel}%`}
			</span>

			<Button
				className="plot-zoom-control-button"
				disabled={zoomLevel >= 200}
				onClick={zoomIn}
			>
				+
			</Button>
		</div>
	);
}

export function LoadingScreen({
	hidden,
	loadingProgress = 0,
	isMobile = false,
}) {
	const getContent = () => {
		if (isMobile) {
			return (
				<div className="loading-wrapper">
					<progress value={loadingProgress} />
					<span>{Math.round(loadingProgress * 1000) / 10}%</span>
				</div>
			);
		}
	};

	return (
		<div
			className={"loading" + (isMobile ? " mobile" : "")}
			hidden={hidden}
		>
			{getContent()}
		</div>
	);
}

export default StackPlots;
